import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Avatar,
  Box,
  CardContent,
  Paper,
  Typography,
  CircularProgress,
  Stack
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import HawkIcon from '../components/ui/HawkIcon';
import api from '../services/api';

const BRAND_BG = '#0f2744';
const BRAND_ACCENT = '#2557d4';

/**
 * Public page opened from RSVP email (Accept / Decline).
 * Matches Hack2skill check-in branding.
 */
export default function RsvpResponsePage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(null);
  const [message, setMessage] = useState('');

  const decisionRaw = (searchParams.get('decision') || '').toLowerCase();
  const decisionLabel = useMemo(() => {
    if (decisionRaw === 'accept') return 'Accept';
    if (decisionRaw === 'decline') return 'Decline';
    return null;
  }, [decisionRaw]);

  useEffect(() => {
    document.title = 'RSVP · Hack2skill';
    return () => {
      document.title = 'Hack2skill Check-In';
    };
  }, []);

  useEffect(() => {
    const token = searchParams.get('token');
    const decision = decisionRaw;

    if (!token || !['accept', 'decline'].includes(decision)) {
      setLoading(false);
      setSuccess(false);
      setMessage(
        'This link is incomplete. Please use the Accept or Decline buttons from your invitation email.'
      );
      return;
    }

    const dedupeKey = `hawkeye_rsvp_${decision}_${token.length}_${token.slice(-12)}`;
    const cached = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(dedupeKey) : null;
    if (cached) {
      try {
        const { ok, msg } = JSON.parse(cached);
        setSuccess(ok);
        setMessage(msg);
      } catch {
        setSuccess(true);
        setMessage('Your response was already recorded.');
      }
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post(
          `/api/public/rsvp/${encodeURIComponent(token)}`,
          { decision },
          { headers: { Accept: 'application/json' } }
        );
        if (!cancelled) {
          const ok = !!data.success;
          const msg = data.message || (data.success ? 'Thank you.' : 'Unable to update your RSVP.');
          setSuccess(ok);
          setMessage(msg);
          try {
            sessionStorage.setItem(dedupeKey, JSON.stringify({ ok, msg }));
          } catch {
            /* ignore quota / private mode */
          }
        }
      } catch (err) {
        const msg =
          err.response?.data?.message ||
          err.message ||
          'We could not update your RSVP. Try again or contact the event organizer.';
        if (!cancelled) {
          setSuccess(false);
          setMessage(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [searchParams, decisionRaw]);

  const showSuccess = !loading && success === true;
  const showError = !loading && success === false;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 3 },
        background: `linear-gradient(165deg, rgba(15, 39, 68, 0.06) 0%, #eff6ff 38%, #f4f6f8 100%)`
      }}
    >
      <Paper
        elevation={0}
        sx={{
          maxWidth: 460,
          width: '100%',
          borderRadius: '16px',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'rgba(30, 58, 138, 0.12)',
          boxShadow: '0 24px 48px rgba(15, 39, 68, 0.08), 0 8px 24px rgba(0, 0, 0, 0.06)'
        }}
      >
        {/* Header — same family as admin sidebar + email */}
        <Box
          sx={{
            background: `linear-gradient(135deg, ${BRAND_BG} 0%, ${BRAND_ACCENT} 100%)`,
            px: 3,
            py: 2.75,
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            borderBottom: '1px solid rgba(255,255,255,0.08)'
          }}
        >
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: '#fff',
              border: '2px solid rgba(255,255,255,0.25)',
              boxShadow: '0 4px 14px rgba(0,0,0,0.15)'
            }}
          >
            <HawkIcon size={44} />
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                color: '#fff',
                fontWeight: 800,
                letterSpacing: '-0.02em',
                lineHeight: 1.2
              }}
            >
              Hack2skill
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                color: 'rgba(255,255,255,0.72)',
                fontWeight: 600,
                letterSpacing: '0.14em',
                textTransform: 'uppercase',
                fontSize: '0.7rem',
                mt: 0.35
              }}
            >
              {decisionLabel ? `RSVP · ${decisionLabel}` : 'RSVP'}
            </Typography>
          </Box>
        </Box>

        <CardContent sx={{ px: { xs: 2.5, sm: 3.5 }, py: 4 }}>
          {loading && (
            <Stack alignItems="center" spacing={2.5} py={1}>
              <CircularProgress
                size={48}
                thickness={4}
                sx={{ color: BRAND_BG }}
              />
              <Typography
                color="text.secondary"
                textAlign="center"
                sx={{ fontSize: '1rem', maxWidth: 320 }}
              >
                Confirming your response…
              </Typography>
            </Stack>
          )}

          {showSuccess && (
            <Stack alignItems="center" spacing={2.25}>
              {decisionRaw === 'decline' ? (
                <CancelOutlinedIcon
                  sx={{
                    fontSize: 64,
                    color: BRAND_BG,
                    opacity: 0.9
                  }}
                />
              ) : (
                <CheckCircleOutlineIcon
                  sx={{
                    fontSize: 64,
                    color: '#0f766e'
                  }}
                />
              )}
              <Typography
                variant="h5"
                component="h1"
                fontWeight={700}
                textAlign="center"
                sx={{ color: '#0f172a', letterSpacing: '-0.02em' }}
              >
                {decisionRaw === 'decline' ? 'Response recorded' : "You're all set"}
              </Typography>
              <Typography
                color="text.secondary"
                textAlign="center"
                lineHeight={1.65}
                sx={{ fontSize: '1rem', maxWidth: 380 }}
              >
                {message}
              </Typography>
              <Stack
                alignItems="center"
                spacing={1}
                sx={{
                  mt: 1,
                  pt: 2.5,
                  width: '100%',
                  borderTop: '1px solid',
                  borderColor: 'divider'
                }}
              >
                <MailOutlineIcon sx={{ color: 'text.disabled', fontSize: 28 }} />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                  sx={{ maxWidth: 340, lineHeight: 1.6 }}
                >
                  You can close this page. No sign-in is required for your RSVP.
                </Typography>
              </Stack>
            </Stack>
          )}

          {showError && (
            <Stack alignItems="center" spacing={2.25}>
              <ErrorOutlineIcon
                sx={{
                  fontSize: 64,
                  color: '#b45309'
                }}
              />
              <Typography
                variant="h5"
                component="h1"
                fontWeight={700}
                textAlign="center"
                sx={{ color: '#0f172a', letterSpacing: '-0.02em' }}
              >
                Could not update RSVP
              </Typography>
              <Typography
                color="text.secondary"
                textAlign="center"
                lineHeight={1.65}
                sx={{ fontSize: '1rem', maxWidth: 380 }}
              >
                {message}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                textAlign="center"
                sx={{ mt: 1, maxWidth: 340 }}
              >
                If this keeps happening, contact the event organizer.
              </Typography>
            </Stack>
          )}
        </CardContent>

        <Box
          component="footer"
          sx={{
            px: 3,
            py: 2,
            bgcolor: 'rgba(6, 40, 30, 0.03)',
            borderTop: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            textAlign="center"
            display="block"
            sx={{ letterSpacing: '0.02em' }}
          >
            Sent by Hack2skill Check-In
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
}
